import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { CreateLabelDialog } from './CreateLabelDialog'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { PickerOption } from '@/components/app/combo-box/PickerPanel'
import { PickerPanel } from '@/components/app/combo-box/PickerPanel'
import { useTaskLabels } from '@/lib/db/rxdb/hooks/entities/task-labels'
import { cn } from '@/lib/utils'

interface LabelSelectionPanelProps {
  availableLabels: Array<LabelDocument>
  labelGroups?: Array<LabelGroupDocument>
  projects?: Array<ProjectDocument>
  selectedLabelIds: Array<string>
  onSelectionChange: (labelIds: Array<string>) => void
  className?: string
  projectId?: string | null
}

const sortByOrderThenName = (
  aOrder?: string | null,
  bOrder?: string | null,
  aName = '',
  bName = '',
) => {
  if (aOrder && bOrder) {
    const result = aOrder.localeCompare(bOrder)
    if (result !== 0) {
      return result
    }
  } else if (aOrder || bOrder) {
    return aOrder ? -1 : 1
  }

  return aName.localeCompare(bName)
}

const byOrderThenName = (
  a: { sortOrder?: string | null; name: string },
  b: { sortOrder?: string | null; name: string },
) => sortByOrderThenName(a.sortOrder, b.sortOrder, a.name, b.name)

function LabelDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="mx-[3px] size-2 flex-none rounded-full"
      style={{ backgroundColor: color }}
    />
  )
}

/**
 * Multi-select label picker on the shared PickerPanel shell (design 31b): the picked
 * labels as chips on top, checkboxes, usage counts, Space to toggle, and `Create "…"`
 * when the typed name is new. Stays open until Esc. Labels in a label group are
 * exclusive within that group.
 */
export function LabelSelectionPanel({
  availableLabels,
  labelGroups = [],
  projects = [],
  selectedLabelIds,
  onSelectionChange,
  className,
  projectId = null,
}: LabelSelectionPanelProps) {
  const { t } = useTranslation('common')
  const [createName, setCreateName] = useState<string | null>(null)
  const { taskLabels } = useTaskLabels()

  const labelsById = useMemo(
    () => new Map(availableLabels.map((label) => [label.id, label])),
    [availableLabels],
  )

  const usageByLabel = useMemo(() => {
    const counts = new Map<string, number>()
    for (const taskLabel of taskLabels) {
      counts.set(taskLabel.labelId, (counts.get(taskLabel.labelId) ?? 0) + 1)
    }
    return counts
  }, [taskLabels])

  // Workspace labels first (loose, then by group), then project labels the same way.
  const options = useMemo<Array<PickerOption>>(() => {
    const groupsById = new Map(labelGroups.map((group) => [group.id, group]))
    const scopes = [
      availableLabels.filter((label) => !label.projectId),
      availableLabels.filter((label) => !!label.projectId),
    ]

    return scopes.flatMap((scopeLabels, scopeIndex) => {
      const loose = scopeLabels.filter((label) => !label.labelGroupId).sort(byOrderThenName)
      const grouped = new Map<string, Array<LabelDocument>>()
      for (const label of scopeLabels) {
        if (!label.labelGroupId) continue
        const list = grouped.get(label.labelGroupId) ?? []
        list.push(label)
        grouped.set(label.labelGroupId, list)
      }
      const groupEntries = Array.from(grouped, ([groupId, labels]) => ({
        name: groupsById.get(groupId)?.name ?? t('picker.labels.group'),
        sortOrder: groupsById.get(groupId)?.sortOrder ?? null,
        labels: labels.sort(byOrderThenName),
      })).sort(byOrderThenName)

      const looseHeading = scopeIndex === 0 ? undefined : t('picker.labels.project')
      const toOption = (label: LabelDocument, group?: string): PickerOption => ({
        value: label.id,
        label: label.name,
        icon: <LabelDot color={label.color} />,
        meta: usageByLabel.get(label.id) || undefined,
        group,
      })

      return [
        ...loose.map((label) => toOption(label, looseHeading)),
        ...groupEntries.flatMap((entry) =>
          entry.labels.map((label) => toOption(label, entry.name)),
        ),
      ]
    })
  }, [availableLabels, labelGroups, usageByLabel, t])

  const handleToggle = (labelId: string) => {
    const label = labelsById.get(labelId)
    if (!label) return
    const groupId = label.labelGroupId

    const nextIds = selectedLabelIds.includes(labelId)
      ? selectedLabelIds.filter((id) => id !== labelId)
      : selectedLabelIds
          .filter((id) => !groupId || labelsById.get(id)?.labelGroupId !== groupId)
          .concat(labelId)

    onSelectionChange(nextIds)
  }

  const selectedLabels = selectedLabelIds
    .map((id) => labelsById.get(id))
    .filter((label): label is LabelDocument => !!label)

  const chips =
    selectedLabels.length > 0 ? (
      <div className="flex flex-wrap gap-1 px-2.5 pb-1 pt-2">
        {selectedLabels.map((label) => (
          <span
            key={label.id}
            className="flex h-[22px] items-center gap-[5px] rounded-full bg-muted pl-2 pr-1 text-xs"
          >
            <span
              aria-hidden
              className="size-2 rounded-full"
              style={{ backgroundColor: label.color }}
            />
            {label.name}
            <button
              type="button"
              aria-label={t('picker.labels.remove', { name: label.name })}
              onClick={() => handleToggle(label.id)}
              className="grid size-4 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
            >
              <X className="size-[11px]" />
            </button>
          </span>
        ))}
      </div>
    ) : null

  return (
    <div data-testid="label-selection-panel" className={cn('flex flex-col', className)}>
      <PickerPanel
        multiple
        options={options}
        selected={selectedLabelIds}
        onSelect={handleToggle}
        searchPlaceholder={t('picker.labels.search')}
        header={chips}
        emptyText={t('picker.labels.empty')}
        onCreate={setCreateName}
        action={{
          label: t('picker.labels.new'),
          icon: <Plus className="size-[13px]" />,
          onSelect: () => setCreateName(''),
        }}
      />

      <CreateLabelDialog
        open={createName !== null}
        onOpenChange={(open) => {
          if (!open) setCreateName(null)
        }}
        initialName={createName ?? ''}
        projectId={projectId}
        projects={projects}
        labelGroups={labelGroups}
        existingLabels={availableLabels}
        onSuccess={(newLabel) => onSelectionChange([...selectedLabelIds, newLabel.id])}
      />
    </div>
  )
}
